import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, KeyRound, Lock, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { authAPI } from '../../services/api';
import AuthShell from '../../components/auth/AuthShell';

const inputBase =
  'block w-full rounded-md border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-100';

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialEmail = useMemo(() => searchParams.get('email') || '', [searchParams]);
  const [formData, setFormData] = useState({
    email: initialEmail,
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword({
        email: formData.email.trim().toLowerCase(),
        code: formData.code.trim(),
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      });
      toast.success('Password reset successfully.');
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(error?.message || 'Password reset failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Set a new password"
      subtitle="Use the six digit code from your email."
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-700">Account email</label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className={`${inputBase} pl-10`}
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="code" className="mb-2 block text-sm font-medium text-slate-700">Reset code</label>
          <div className="relative">
            <KeyRound className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
            <input
              id="code"
              name="code"
              value={formData.code}
              onChange={handleChange}
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              className={`${inputBase} pl-10`}
              placeholder="000000"
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="newPassword" className="mb-2 block text-sm font-medium text-slate-700">New password</label>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
            <input
              id="newPassword"
              name="newPassword"
              type="password"
              value={formData.newPassword}
              onChange={handleChange}
              className={`${inputBase} pl-10`}
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="mb-2 block text-sm font-medium text-slate-700">Confirm password</label>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`${inputBase} pl-10`}
              required
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? 'Resetting password...' : 'Reset password'}
          {!loading && <Check className="h-4 w-4" />}
        </button>
      </form>

      <div className="mt-6 border-t border-slate-200 pt-5 text-center text-sm text-slate-600">
        <Link to="/login" className="inline-flex items-center gap-2 font-medium text-sky-700 hover:text-sky-800">
          <ArrowLeft className="h-4 w-4" />
          Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
}

export default ResetPasswordPage;
